import React from 'react';
import { DatagridRow, FunctionField, RecordContextProvider, useListContext, useTranslate } from 'react-admin';
import { makeStyles, Tooltip } from '@material-ui/core';
import { Check, Close } from '@material-ui/icons';

import { isAvailability, isDraftGame, isGame, isPractice, isSlot } from '../../common/calendar/EventDetails';
import TimeRangeField from '../../common/fields/TimeRangeField';
import TextArrayField from '../../common/fields/TextArrayField';
import { OfficeField } from '../offices/OfficeField';
import { CategoryField } from '../categories/CategoryField';
import { TeamsField } from '../teams/TeamsField';
import { ScheduleField } from '../schedules/ScheduleField';
import { CategoriesField } from '../categories/CategoriesField';
import { OfficesField } from '../offices/OfficesField';
import { useIsHomeFirst } from '../events/EventViewSettings';
import { TeamField } from '../teams/TeamField';
import { SURFACE_EVENT_TYPES } from '@hisports/common/src/constants';

const useStyles = makeStyles(theme => ({
  field: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '10% 10% 10% 50% 20%',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: ({ record }) => getEventTypeColor(record?.type, theme, record?.isAvailable),
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    }
  },
  group: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  matchUp: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  gameDetails: {
    display: 'flex',
    flexDirection: 'column',
  }
}));

export const getEventTypeColor = (type, theme, isAvailable, isHover) => {
  switch (type) {
    case SURFACE_EVENT_TYPES.GAME:
      return theme.palette.lightBlue[isHover ? 200 : 100];
    case SURFACE_EVENT_TYPES.DRAFTGAME:
      return theme.palette.lightBlue[isHover ? 100 : 50];
    case SURFACE_EVENT_TYPES.PRACTICE:
      return theme.palette.purple[isHover ? 100 : 50];
    case SURFACE_EVENT_TYPES.AVAILABILITY:
      return isAvailable ? theme.palette.success[isHover ? 100 : 50] : theme.palette.error[isHover ? 100 : 50];
    case SURFACE_EVENT_TYPES.TIMESLOT:
      return isHover ? theme.palette.grey[50] : 'white'
    default:
      return null
  }
}

const AvailabilityStatus = ({ record }) => {
  const classes = useStyles();
  const translate = useTranslate();
  const { isAvailable } = record || {};

  const text = translate(`ra.message.${isAvailable ? 'available' : 'unavailable'}`);

  return <div className={classes.group}>
    {text}
    {isAvailable ? <Check fontSize="small" /> : <Close fontSize="small" />}
  </div>
}

const IdentifierField = () => {
  return <FunctionField
    source="id"
    render={record => {
      if (isGame(record)) return record.number;
      if (isDraftGame(record)) return record.id;
      if (isPractice(record)) return record.name;
      if (isSlot(record)) return record.name;
      if (isAvailability(record)) return <AvailabilityStatus record={record} />;
      return record.id;
    }}
  />
}

const SlotDetails = ({ record }) => {
  const items = [];

  if (record?.types?.length) {
    items.push(<TextArrayField key="types" record={record} source="types" />);
  }

  if (record?.categoryIds?.length) {
    items.push(<CategoriesField key="categories" record={record} source="categoryIds" link={false} />);
  }

  if (record?.leagueIds?.length) {
    items.push(<OfficesField key="leagues" record={record} source="leagueIds" />);
  }

  return (
    <RecordContextProvider value={record}>
      {items.map((item, index) => (
        <span key={item.id}>
          {index > 0 && ' • '}
          {item}
        </span>
      ))}
    </RecordContextProvider>
  );
};

const MatchUp = () => {
  const classes = useStyles();
  const isHomeFirst = useIsHomeFirst();

  return (
    <div className={classes.matchUp}>
      <TeamField source={isHomeFirst ? "homeTeamId" : "awayTeamId"} link={false} />
      <span>{isHomeFirst ? 'vs' : '@'}</span>
      <TeamField source={isHomeFirst ? "awayTeamId" : "homeTeamId"} link={false} />
    </div>
  );
};

const GameDetailsField = ({ record }) => {
  const classes = useStyles();
  return (
    <RecordContextProvider value={record}>
      <div className={classes.gameDetails}>
        <div>
          <ScheduleField source="scheduleId" link={false} />
          {' • '}
          <CategoryField source="categoryId" genderColor="inherit" />
        </div>
        <MatchUp />
      </div>
    </RecordContextProvider>
  );
};

const PracticeDetailsField = ({ record }) => {
  if (!record?.teamIds?.length) return null;
  return <RecordContextProvider value={record}>
    <TeamsField record={record} source="teamIds" link={false} />
  </RecordContextProvider>
}

const DetailsField = () => {
  return <FunctionField
    source="id"
    render={(record) => {
      if (isGame(record) || isDraftGame(record)) return <GameDetailsField record={record} />;
      if (isPractice(record)) return <PracticeDetailsField record={record} />;
      if (isSlot(record)) return <SlotDetails record={record} />;
      if (isAvailability(record)) return record.notes;
      return record.id;
    }}
  />
}

const TypeField = () => {
  const translate = useTranslate();

  return <FunctionField
    source="type"
    render={({ type }) => translate(`resources.surfaceEvents.values.type.${type}`)}
  />
}

const Cell = ({ children }) => {
  const classes = useStyles();

  return <Tooltip title={children || ''} placement="bottom-start">
    <div className={classes.field}>
      {children}
    </div>
  </Tooltip>
}

export const SurfaceEventRow = ({
  event,
  isRowSelectable,
  rowClick = 'show',
  ...props
}) => {
  const classes = useStyles({ record: event });
  const { selectedIds, onToggleItem } = useListContext();
  const selected = selectedIds.includes(event.id);
  const selectable = !isRowSelectable || isRowSelectable(event.id);

  return (
    <DatagridRow
      {...props}
      record={event}
      id={event.id}
      rowClick={false}
      selected={selected}
      selectable={selectable}
      onToggleItem={onToggleItem}
      className={classes.row}
      hover={false}
    >
      <Cell>
        <TimeRangeField startSource="startTime" endSource="endTime" record={event} />
      </Cell>
      <Cell>
        <TypeField />
      </Cell>
      <Cell>
        <IdentifierField record={event} />
      </Cell>
      <Cell>
        <DetailsField record={event} />
      </Cell>
      <Cell>
        <OfficeField source="officeId" record={event} link={false} />
      </Cell>
    </DatagridRow>
  );
};
